import { createPublicClient, http } from "viem"
import { createConfig } from "wagmi"
import {
  mainnet,
  arbitrum,
  arbitrumSepolia,
  base,
  baseSepolia,
  sepolia,
} from "viem/chains"
import { connectorsForWallets } from '@rainbow-me/rainbowkit'
import {
  metaMaskWallet,
  rainbowWallet,
  walletConnectWallet,
  coinbaseWallet,
  trustWallet,
  okxWallet
} from "@rainbow-me/rainbowkit/wallets"


export enum CryptexProducts {
  Crypdex = "CRYPDEX",
  Governance = "GOVERNANCE",
  Indexes = "INDEXES",
  Perpetuals = "PERPETUALS",
  PerpetualsV1 = "PERPETUALS_V1",
  V1 = "V1",
  Spot = "SPOT",
}

export const DefaultChain = arbitrum;

export const SupportedChainIds = [mainnet.id, sepolia.id, arbitrum.id, arbitrumSepolia.id, base.id, baseSepolia.id]

export const chainIdToChainMap = {
  [mainnet.id]: mainnet,
  [sepolia.id]: sepolia,
  [arbitrum.id]: arbitrum,
  [arbitrumSepolia.id]: arbitrumSepolia,
  [base.id]: base,
  [baseSepolia.id]: baseSepolia,
}

export type SupportedChainIdType = (typeof SupportedChainIds)[number];

export const isSupportedChain = (chainId: number): boolean => {
  const chainsIds = SupportedChainIds as Array<number>
  return chainsIds.includes(chainId)
}

export const IndexesGraphUrls: { [chainId in SupportedChainIdType]: string } = {
  [mainnet.id]: "",
  [sepolia.id]: "https://subgraph.satsuma-prod.com/0b9d5e79fdb9/cryptex/cryptex-base/api",
  [arbitrum.id]: "",
  [arbitrumSepolia.id]: "",
  [base.id]: "https://subgraph.satsuma-prod.com/0b9d5e79fdb9/cryptex/cryptex-base/api",
  [baseSepolia.id]: "https://subgraph.satsuma-prod.com/0b9d5e79fdb9/cryptex/cryptex-base/api",
}

export const V1GraphUrls: { [chainId in SupportedChainIdType]: string } = {
  [mainnet.id]: process.env.REACT_APP_V1_GRAPH_URL_MAINNET ?? "",
  [sepolia.id]: process.env.REACT_APP_V1_GRAPH_URL_SEPOLIA ?? "",
  [arbitrum.id]: process.env.REACT_APP_V1_GRAPH_URL_ARBITRUM ?? "",
  [arbitrumSepolia.id]: process.env.REACT_APP_V1_GRAPH_URL_ARBITRUM_SEPOLIA ?? "",
  [base.id]: "",
  [baseSepolia.id]: "",
};

export const FuulGraphUrl = "https://subgraph.satsuma-prod.com/64a0e71a7397/fuul-team--611570/base/api"

const WalletConnectProjectId = process.env.REACT_APP_WALLET_CONNECT_ID || "";

export const AlchemyKey = process.env.REACT_APP_ALCHEMY_KEY || ""
export const AlchemySepoliaKey = process.env.REACT_APP_ALCHEMY_KEY || ""
export const AlchemyArbitrumKey = process.env.REACT_APP_ALCHEMY_ARBITRUM_KEY || ""
export const AlchemyArbitrumSepoliaKey = process.env.REACT_APP_ALCHEMY_ARBITRUM_KEY_SEPOLIA || ""
export const AlchemyBaseKey = process.env.REACT_APP_ALCHEMY_BASE_KEY || ""
export const AlchemyBaseSepoliaKey = process.env.REACT_APP_ALCHEMY_BASE_KEY_SEPOLIA || ""


export const rpcUrls: { [chainId in SupportedChainIdType]: string } = {
  [arbitrum.id]: `https://arb-mainnet.g.alchemy.com/v2/${AlchemyArbitrumKey}`,
  [arbitrumSepolia.id]: `https://arb-sepolia.g.alchemy.com/v2/${AlchemyArbitrumSepoliaKey}`,
  [base.id]: `https://base-mainnet.g.alchemy.com/v2/${AlchemyBaseKey}`,
  [baseSepolia.id]: `https://base-sepolia.g.alchemy.com/v2/${AlchemyBaseSepoliaKey}`,
  [mainnet.id]: `https://eth-mainnet.g.alchemy.com/v2/${AlchemyKey}`,
  [sepolia.id]: `https://eth-sepolia.g.alchemy.com/v2/${AlchemySepoliaKey}`,
}

export const getPublicClient = (chainId: SupportedChainIdType) => { 
  return createPublicClient({
    batch: {
      multicall: true,
    },
    chain: chainIdToChainMap[chainId],
    transport: http(rpcUrls[chainId])
  })
}

const connectors = connectorsForWallets([{
    groupName: "Recommended",
    wallets: [metaMaskWallet, walletConnectWallet, rainbowWallet, coinbaseWallet, okxWallet, trustWallet],
  }],
  {
    appName: 'Cryptex Finance',
    projectId: WalletConnectProjectId,
  }
);

export const wagmiConfig = createConfig({
  ssr: false,
  chains: [
    mainnet,
    arbitrum, 
    base,
  ],
  transports: {
    [mainnet.id]: http(rpcUrls[mainnet.id]),
    [arbitrum.id]: http(rpcUrls[arbitrum.id]),
    [base.id]: http(rpcUrls[base.id]),
  },
  connectors,
  batch: {
    multicall: true,
  },
})

export const CryptexPricesUrl = "https://api.perps.cryptex.finance"
export const CryptexPriceFeedUrl = 'https://api.perps.cryptex.finance/price-feed'


export const metamaskTxRejectedError = "user rejected"

export const BetaWhitelist = [
  "0xd4fa23307a181B9ca567886eB5bCd5c8f8f8bB3E",
  "0xF6a16a48099497C59e8abEAa37Bb37B2F9B793d4",
  "0xcF3E2Ee2b0cD1194ba33cD1c7d9614DC635a1714",
  "0xF8E3b46Ed9efD7e28a0fcB1B62F76747474f5018",
  "0xa9c8CD8B3E6E24446D7dFDFd63563FA50852ec3c",
  "0xD43Ca531907f8C8dBb3272Cc2EF321cC572E56E3",
  "0xF211d99364f899acC598F46d1f1456c80B9F0586",
  // users
  "0xc0167c8A4aF0CFa074B384e29F4d1Df3b7e16165",
  "0xBc74dA5a258a3aABA1c75c68EFB08c9f37537be8",
  "0xf52bfddcCe6D44a3C8a0c1c968dF68969b4A05B8",
  "0xf6b446e2c1437a0e1832ccf7e09cbd8304a6fadb",
  "0x75ef38c98e81b267f059c4a78ab2594d4915f1be",
  "0x2A1d6415F5C7bAb3C51EE23BFD9E9B9921526A48",
  "0xf52bfddcCe6D44a3C8a0c1c968dF68969b4A05B8",
]

export const notAllowedCountries = [
  "BY",
  "CD",
  "CI",
  "CU",
  "IR",
  "IQ",
  "LB",
  "LR",
  "LY",
  "KP",
  "MM",
  "SD",
  "SS",
  "SY",
  "RU",
  "VE",
  "YE",
  "ZW",
]